/* „Podgląd trajektorii" — the mock's `previewBox`, its scenario segment and the chart inside it.
 *
 * The box is COLLAPSIBLE and its head is the toggle, exactly as the mock draws it. It reserves the
 * chart's height while it is open rather than letting the SVG size itself from its data: the plot is
 * a fixed `viewBox` scaled to the box's width, so a rule change re-inks it and never re-lays it out.
 */

.box {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  padding: var(--space-6px) var(--space-10px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.head {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  width: 100%;
  padding: var(--space-0);
  text-align: start;
  cursor: pointer;
  background: none;
  border: none;
}

.head:focus-visible {
  outline: var(--border-ring) solid var(--accent);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-xs);
}

.chevron {
  display: grid;
  place-items: center;
  width: var(--space-20px);
  height: var(--space-20px);
  font-size: var(--fs-note);
  color: var(--text-2);
  border-radius: var(--radius-5px);
  transition: rotate var(--dur-fast) var(--ease-default);
}

.head[aria-expanded='true'] .chevron {
  rotate: 180deg;
}

@media (prefers-reduced-motion: reduce) {
  .chevron {
    transition: none;
  }
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.scenarioRow {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
}

.segment {
  display: grid;
  flex: none;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3px);
  min-width: var(--w-prog-scenario);
  padding: var(--space-3px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
}

.option {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  justify-content: center;
  height: var(--h-prog-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
}

.option[aria-checked='true'] {
  color: var(--bg-card);
  background: var(--text);
}

.option:focus-visible {
  outline: var(--border-ring) solid var(--accent);
  outline-offset: var(--space-1px);
}

.optionDot {
  display: block;
  flex: none;
  width: var(--space-6px);
  height: var(--space-6px);
  border-radius: var(--r-circle);
}

.optionDot[data-scenario='delivers'] {
  background: var(--prog-sheet-static);
}

.optionDot[data-scenario='fallsShort'] {
  background: var(--prog-sheet-manual);
}

.spring {
  flex: 1;
}

/* The chart's own recess — the mock's `chartWrap3`, a step deeper than the box around it. */
.plot {
  position: relative;
  display: flex;
  align-items: center;
  padding: var(--space-1);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.svg {
  display: block;
  width: 100%;
  height: auto;
}

/* ── Inside the SVG ──────────────────────────────────────────────────────────────────────── */

.gridLine {
  stroke: var(--border);
  stroke-width: 1;
}

/* The mock's own guide over the cycle the client is standing in: brighter, thicker and dashed
 * (`stroke: rgba(255,255,255,.35); strokeWidth: 1.5; strokeDasharray: '3 3'`). */
.gridLine[data-now='true'] {
  stroke: var(--border-2);
  stroke-width: 1.5;
  stroke-dasharray: 3 3;
}

.line {
  fill: none;
  stroke: var(--prog-sheet-static);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* The lived run: SOLID, in neutral ink. It is not a prediction, so it carries no family colour and
 * no dash — the owner's „real execution told apart from the simulation", 15 September 2026. */
.line[data-real='true'] {
  stroke: var(--text);
}

.line[data-family='fromResult'] {
  stroke: var(--prog-sheet-result);
  stroke-dasharray: 5 4;
}

/* No rule at all: every cycle repeats cycle one, which is neither a certain number nor a prediction.
 * The mock draws that run in a READABLE neutral — its own `#8a8a93`, the grey it words every
 * secondary thing in, which is `--text-2` here. It took `--prog-sheet-base` until 15 September 2026,
 * and that token is the matrix CELL's base border (`#3a3a40` in the dark theme): a line at four
 * percent of the plot's own ground, which is a run the trainer cannot see. */
.line[data-family='none'] {
  stroke: var(--text-2);
}

.node {
  fill: var(--bg-sunken);
  stroke: var(--prog-sheet-static);
  stroke-width: 2;
}

.node[data-family='fromResult'] {
  stroke: var(--prog-sheet-result);
}

.node[data-family='none'] {
  stroke: var(--text-3);
}

/* A SIMULATED cycle the invented client fell short of. It is the DELIVERY verdict, so it reads the
 * verdict pair rather than `--prog-sheet-manual`, which means „the trainer pinned this cell". */
.node[data-delivered='false'] {
  stroke: var(--prog-sheet-short);
}

/* A node that is a session rather than a projection. Neutral, filled to the same ground as the rest
 * so the run reads as one line, and its verdict is carried by the badge beside it — which every
 * point on this plot already has, and which spells the answer as a glyph rather than as a hue.
 *
 * It sits AFTER the shortfall rule on purpose: the two match the same node on a lived cycle that
 * fell short, at equal specificity, so source order decides — and while this rule came first, every
 * such node was drawn in the shortfall pink the comment above says it does not take. */
.node[data-real='true'] {
  stroke: var(--text);
}

/* The verdict mark. Filled rather than outlined so it reads as a stamp on the cycle rather than a
 * second data point, which is exactly how the mock draws it. The pair is the DELIVERY verdict's
 * own, for the reason `--prog-sheet-ok` exists: „the engine computed this" and „the client cleared
 * the bar" are two claims that happen to share a hex. */
.verdict {
  fill: var(--prog-sheet-ok);
  stroke: none;
}

.verdict[data-delivered='false'] {
  fill: var(--prog-sheet-short);
}

.verdictGlyph {
  font-size: var(--fs-prog-chart-caption);
  font-weight: var(--fw-bold);
  fill: var(--bg-sunken);
}

/* The mock's `popIn`, per point, at its own half second. Transform and opacity only; the plot's box
 * is fixed. */
.point {
  transform-box: fill-box;
  transform-origin: center;
  animation: point-in var(--dur-point-in) var(--ease-default) both;

  /* The node's POSITION travels, never its milliseconds: a duration literal in a component is a
   * duration nothing else can find, and the plot's own stagger is `--dur-point-stagger`. */
  animation-delay: calc(var(--point-index, 0) * var(--dur-point-stagger));
}

/* The mock's `markIn`: the verdict lands a fifth of a second after the node it judges, which is what
 * makes it read as a verdict ON the cycle rather than as part of the plot arriving. */
.mark {
  transform-box: fill-box;
  transform-origin: center;
  animation: mark-in var(--dur-mark-in) ease both;
  animation-delay: calc(
    var(--point-index, 0) * var(--dur-point-stagger) + var(--dur-mark-delay)
  );
}

@keyframes point-in {
  from {
    opacity: 0;
    scale: 0.6;
  }

  to {
    opacity: 1;
    scale: 1;
  }
}

@keyframes mark-in {
  from {
    opacity: 0;
    scale: 0.4;
  }

  to {
    opacity: 1;
    scale: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .point,
  .mark {
    animation: none;
  }
}

.label {
  font-size: var(--fs-prog-chart-label);
  font-weight: var(--fw-semibold);
  fill: var(--text);
  paint-order: stroke;
  stroke: var(--bg-sunken);
  stroke-width: 3;
  stroke-linejoin: round;
}

.doneLabel {
  font-size: var(--fs-prog-chart-label);
  font-weight: var(--fw-semibold);
  fill: var(--text-2);
  paint-order: stroke;
  stroke: var(--bg-sunken);
  stroke-width: 3;
  stroke-linejoin: round;
}

.doneLabel[data-tone='delivered'] {
  fill: var(--prog-sheet-ok-ink);
}

.doneLabel[data-tone='missed'] {
  fill: var(--prog-sheet-short-ink);
}

.caption {
  font-size: var(--fs-prog-chart-caption);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;

  /* The mock's own `.08em` on this caption, not the eyebrow's `.14em`: the stack is four lines deep
   * and the wider tracking pushed „ZROBIŁ · GÓRA ✓" into its neighbour at five cycles. */
  letter-spacing: var(--ls-group);
  fill: var(--text-2);
  paint-order: stroke;
  stroke: var(--bg-sunken);
  stroke-width: 3;
  stroke-linejoin: round;
}

/* „↓powt." stays as it is written — see the chart's own note. */
.captionMark {
  text-transform: none;
}

.caption[data-tone='delivered'] {
  fill: var(--prog-sheet-ok-ink);
}

.caption[data-tone='missed'] {
  fill: var(--prog-sheet-short-ink);
}

.axisLabel {
  font-size: var(--fs-prog-chart-axis);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  fill: var(--text-2);
}

/* „C3 · teraz", in the mock's own full ink against every other cycle's secondary grey. */
.axisLabel[data-now='true'] {
  font-weight: var(--fw-bold);
  fill: var(--text);
}

/* The mock's „↻ Odtwórz" at the right end of the scenario row. It replays the plot's entrance, which
 * is what a trainer asks to see again after moving the step. A plain ghost — the accent marks a real
 * action and this one changes nothing. */
.replay {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-prog-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.replay:focus-visible {
  outline: var(--border-ring) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* The mock's `narrationH`: one sentence under the plot saying what the rule does, in the same words
 * the sentence at the top of the sheet uses. `trajectoryNarration` words both. */
.summary {
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint);
  color: var(--text-2);
}
